import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Inbox, Search, Building2, Mail, Loader2, RefreshCw } from "lucide-react";
import { LogoMark } from "@/components/LogoMark";

const typeStyles = {
  council: "bg-[#121316] text-white",
  consultant: "bg-[#E6EBF5] text-[#0033A0]",
  other: "bg-zinc-100 text-zinc-700",
};

export default function AdminLeads() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/leads");
      setLeads(res.data);
    } catch (err) {
      if (err?.response?.status === 401) navigate("/admin/login");
      else toast.error("Could not load leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {}
    navigate("/admin/login");
  };

  const filtered = leads.filter((l) => {
    const matchesType = filter === "all" || l.user_type === filter;
    const hay = `${l.name} ${l.email} ${l.organization} ${l.message}`.toLowerCase();
    return matchesType && hay.includes(q.toLowerCase());
  });

  const counts = {
    all: leads.length,
    council: leads.filter((l) => l.user_type === "council").length,
    consultant: leads.filter((l) => l.user_type === "consultant").length,
    other: leads.filter((l) => l.user_type === "other").length,
  };

  return (
    <div data-testid="admin-leads-page" className="min-h-screen bg-[#F9F9F8]">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="h-8 w-8 grid place-items-center bg-[#0033A0] text-white">
              <LogoMark size={18} />
            </span>
            <span className="font-heading text-lg font-semibold tracking-tight text-zinc-900">
              WebADAC <span className="text-zinc-400 font-normal">/ Admin</span>
            </span>
          </div>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="inline-flex items-center gap-2 text-sm font-medium text-zinc-600 hover:text-[#0033A0] transition-colors"
          >
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight text-zinc-900 flex items-center gap-3">
              <Inbox size={28} className="text-[#0033A0]" /> Leads inbox
            </h1>
            <p className="mt-2 text-sm text-zinc-600">
              {counts.all} total demo request{counts.all === 1 ? "" : "s"} · Councils {counts.council} · Consultants {counts.consultant} · Other {counts.other}
            </p>
          </div>
          <button
            data-testid="admin-refresh-button"
            onClick={load}
            className="inline-flex items-center gap-2 bg-white border border-zinc-300 hover:bg-zinc-50 px-4 py-2.5 text-sm font-medium rounded-sm transition-colors"
          >
            <RefreshCw size={15} /> Refresh
          </button>
        </div>

        <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input
              data-testid="admin-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, email, organization…"
              className="w-full pl-9 pr-4 py-2.5 rounded-sm bg-white border border-zinc-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#0033A0]"
            />
          </div>
          <div className="flex gap-2">
            {["all", "council", "consultant", "other"].map((t) => (
              <button
                key={t}
                data-testid={`admin-filter-${t}`}
                onClick={() => setFilter(t)}
                className={`px-3.5 py-2 text-xs font-medium rounded-sm border transition-colors capitalize ${
                  filter === t
                    ? "bg-[#0033A0] text-white border-[#0033A0]"
                    : "bg-white text-zinc-600 border-zinc-300 hover:bg-zinc-50"
                }`}
              >
                {t} ({counts[t]})
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6">
          {loading ? (
            <div className="grid place-items-center py-24">
              <Loader2 className="animate-spin text-[#0033A0]" size={28} />
            </div>
          ) : filtered.length === 0 ? (
            <div data-testid="admin-empty-state" className="rounded-2xl border border-dashed border-zinc-300 bg-white py-20 text-center">
              <Inbox size={36} className="mx-auto text-zinc-300" />
              <p className="mt-4 font-heading text-lg text-zinc-700">No leads yet</p>
              <p className="mt-1 text-sm text-zinc-500">New demo requests will appear here.</p>
            </div>
          ) : (
            <div className="grid gap-4" data-testid="admin-leads-list">
              {filtered.map((l) => (
                <div
                  key={l.id}
                  data-testid="admin-lead-card"
                  className="rounded-xl border border-zinc-200 bg-white p-6 hover:border-zinc-400 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="flex items-center gap-3">
                        <h3 className="font-heading font-medium text-zinc-900">{l.name}</h3>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${typeStyles[l.user_type] || typeStyles.other}`}>
                          {l.user_type}
                        </span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-zinc-600">
                        <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1.5 hover:text-[#0033A0]">
                          <Mail size={14} /> {l.email}
                        </a>
                        <span className="inline-flex items-center gap-1.5">
                          <Building2 size={14} /> {l.organization}
                        </span>
                      </div>
                    </div>
                    <span className="text-xs text-zinc-400 font-mono">
                      {new Date(l.created_at).toLocaleString()}
                    </span>
                  </div>
                  <p className="mt-4 text-sm text-zinc-700 leading-relaxed border-l-2 border-zinc-200 pl-4">
                    {l.message}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
